import React from 'react';

/**
 * QuestTracker — small fixed banner that surfaces the player's current
 * primary objective on top of the map. Tutorial quests take precedence
 * (server-side ordering) so new players always see "go here next" hints.
 *
 * Click to open the Compendium quest log (same as pressing J).
 *
 * Props:
 *   questSummary — { active, available, activeObjective, activeQuest }
 *   onClick      — callback to open quest log
 *   style        — optional style overrides (e.g. 'absolute' inside a HUD,
 *                  vs the default 'fixed' for the desktop layout)
 */
const QuestTracker = ({ questSummary, onClick, style }) => {
  if (!questSummary || !questSummary.activeObjective) return null;

  const aq = questSummary.activeQuest || null;
  const hint = aq ? buildHint(aq) : null;
  // v643.2: progress fraction (e.g. "Step 2/5") if quest has multiple stages
  const progress = (aq && aq.totalStages > 1 && aq.stageIndex > 0)
    ? `Step ${aq.stageIndex}/${aq.totalStages}`
    : null;

  return (
    <button
      type="button"
      onClick={onClick}
      title="Open Quest Log (J)"
      className="quest-tracker-banner"
      style={{
        position: 'fixed',
        top: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(0,0,0,0.78)',
        border: '1px solid rgba(255,215,0,0.4)',
        borderRadius: 8,
        padding: '6px 14px',
        color: '#ffd700',
        fontSize: '0.82rem',
        fontWeight: 600,
        zIndex: 1000,
        cursor: 'pointer',
        maxWidth: '92vw',
        textAlign: 'center',
        textShadow: '0 1px 4px rgba(0,0,0,0.8)',
        fontFamily: 'inherit',
        ...style,
      }}
    >
      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {questSummary.activeObjective}
        {progress && (
          <span style={{ marginLeft: 8, fontSize: '0.72rem', color: '#9be09b', fontWeight: 500 }}>
            · {progress}
          </span>
        )}
      </div>
      {hint && (
        <div style={{
          fontSize: '0.72rem',
          fontWeight: 400,
          color: '#cfd8e8',
          marginTop: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          → {hint}
        </div>
      )}
    </button>
  );
};

function buildHint(aq) {
  // Prefer destination area if known and friendly-named; otherwise giver location
  const dest = humanizeArea(aq.destinationArea);
  if (dest) return `Head to ${dest}`;
  if (aq.giver && aq.location) return `See ${aq.giver} at ${aq.location}`;
  if (aq.giver) return `See ${aq.giver}`;
  if (aq.location) return aq.location;
  return null;
}

// Map raw .dat / area keys to readable hints. Anything unmapped falls
// through so we don't expose "maze7.dat" in the UI.
const AREA_NAMES = {
  antaria_city: 'Antaria City',
  maze1: 'Castle Antaria',
  maze7: 'the Western Wilds',
  maze4: 'the Eastern Roads',
  antaria4: 'Antaria — East Gate',
  antaria2: 'Antaria — Town Square',
  antaria3: 'Antaria — Marketplace',
};

function humanizeArea(area) {
  if (!area || typeof area !== 'string') return null;
  // Normalize: strip .dat suffix for table lookup
  const key = area.replace(/\.dat$/i, '');
  if (AREA_NAMES[key]) return AREA_NAMES[key];
  // Friendly fallback: don't expose raw filenames or maze IDs
  if (/^(maze|antaria)\d/i.test(key)) return null;
  // Pass through human-friendly slugs (snake_case → words)
  return key.replace(/_/g, ' ');
}

export default QuestTracker;
